import { useTranslation } from "react-i18next";
import { usePageSEO } from "@/lib/use-page-seo";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MegaFooter from "@/components/MegaFooter";
import { WC2026_TEAMS, GROUPS } from "../../../server/teams";
import { groupPath } from "../../../shared/seo-routes";

export default function GroupsIndex() {
  usePageSEO("seo.group.index_title", "/groups");
  const { t } = useTranslation();
  const lp = useLocalePath();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground">
              {t("nav.back")}
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
          <a href={lp("/")} className="hover:underline">{t("seo.common.home")}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{t("seo.group.index_h1")}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {t("seo.group.index_h1")}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-10">
          {t("seo.group.index_intro")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GROUPS.map((letter) => {
            const teams = WC2026_TEAMS.filter((tm) => tm.group === letter);
            return (
              <a key={letter}
                 href={lp(groupPath(letter.toLowerCase()))}
                 className="block rounded-lg border border-border/50 p-4 hover:bg-muted/30 hover:border-border transition-colors">
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="text-2xl font-bold uppercase"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {t("seo.team.group_label", { group: letter })}
                  </h2>
                </div>
                <ul className="space-y-1.5">
                  {teams.map((team) => (
                    <li key={team.code} className="flex items-center gap-2 text-sm">
                      <span className="text-lg" aria-hidden="true">{team.flag}</span>
                      <span className="text-foreground/85">{team.name}</span>
                    </li>
                  ))}
                </ul>
              </a>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <a href={lp("/")}
             className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90">
            {t("seo.common.cta_pick_teams")}
          </a>
          <a href={lp("/teams")}
             className="inline-flex items-center px-5 py-2.5 rounded-md border border-border text-sm hover:bg-muted/30">
            {t("seo.team.index_h1")}
          </a>
        </div>
      </main>

      <MegaFooter />
    </div>
  );
}
